import { useCallback, useEffect, useRef, useState } from "react";
import api, { errorMessage } from "./api";
import { moveInList } from "./adminLogic";
import { ConfirmButton, Empty, Feedback, Hint, PageTitle, SecondaryButton } from "./ui";
import { GALLERY_FILTERS, galleryImageUrl } from "../config/photos";

const TAGS = GALLERY_FILTERS.filter((tag) => tag !== "Alle");

const GalleryRow = ({ image, index, count, moving, onMove, onSave, onDelete }) => {
    const [caption, setCaption] = useState(image.caption);

    useEffect(() => setCaption(image.caption), [image.caption]);

    return (
        <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-3 border-b border-forest/10 py-3">
            <img
                src={galleryImageUrl(image)}
                alt={image.caption || "Galeriebild"}
                loading="lazy"
                className="aspect-square w-24 object-cover"
            />
            <div className="grid min-w-0 gap-2">
                <div className="grid grid-cols-[minmax(0,1fr)_110px] gap-2">
                    <input
                        className="admin-input !py-2 !text-[15px]"
                        aria-label={`Bildunterschrift Bild ${index + 1}`}
                        placeholder="Bildunterschrift"
                        maxLength={120}
                        value={caption}
                        onChange={(e) => setCaption(e.target.value)}
                        onBlur={() => caption !== image.caption && onSave(image, { caption })}
                    />
                    <select
                        className="admin-input !py-2 !text-[15px]"
                        aria-label={`Kategorie Bild ${index + 1}`}
                        value={image.tag}
                        onChange={(e) => onSave(image, { tag: e.target.value })}
                    >
                        {TAGS.map((tag) => (
                            <option key={tag} value={tag}>
                                {tag}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                    <button
                        type="button"
                        disabled={moving || index === 0}
                        onClick={() => onMove(index, -1)}
                        aria-label={`Bild ${index + 1} nach vorne`}
                        className="h-10 w-10 border border-forest/20 bg-transparent disabled:opacity-30"
                    >
                        ↑
                    </button>
                    <button
                        type="button"
                        disabled={moving || index === count - 1}
                        onClick={() => onMove(index, 1)}
                        aria-label={`Bild ${index + 1} nach hinten`}
                        className="h-10 w-10 border border-forest/20 bg-transparent disabled:opacity-30"
                    >
                        ↓
                    </button>
                    <span className="ml-1 text-xs text-forest/50">Position {index + 1}</span>
                    <label className="ml-2 flex min-h-[40px] items-center gap-2 text-xs">
                        <input
                            type="checkbox"
                            checked={Boolean(image.placeholder)}
                            onChange={(e) => onSave(image, { placeholder: e.target.checked })}
                            className="h-[18px] w-[18px] accent-forest"
                        />
                        Platzhalter
                    </label>
                    <ConfirmButton onConfirm={() => onDelete(image)} className="ml-auto">
                        Entfernen
                    </ConfirmButton>
                </div>
            </div>
        </div>
    );
};

export default function GalleryPage() {
    const [images, setImages] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [moving, setMoving] = useState(false);
    const [message, setMessage] = useState(null);
    const fileInput = useRef(null);

    const load = useCallback(async () => {
        const response = await api.get("/gallery");
        setImages(response.data);
        setLoaded(true);
    }, []);

    useEffect(() => {
        load().catch(() => {
            setLoaded(true);
            setMessage({ error: true, text: "Galerie konnte nicht geladen werden." });
        });
    }, [load]);

    const upload = async (files) => {
        if (!files.length) return;
        const data = new FormData();
        Array.from(files).forEach((file) => data.append("images", file));
        setUploading(true);
        setMessage(null);
        try {
            const response = await api.post("/gallery", data);
            setMessage({ text: `${response.data.length} Foto(s) hochgeladen.` });
            await load();
        } catch (err) {
            setMessage({ error: true, text: errorMessage(err, "Upload fehlgeschlagen.") });
            load();
        } finally {
            setUploading(false);
            fileInput.current.value = "";
        }
    };

    const save = async (image, changes) => {
        setMessage(null);
        try {
            const response = await api.put(`/gallery/${image._id}`, changes);
            setImages((list) => list.map((i) => (i._id === image._id ? response.data : i)));
        } catch (err) {
            setMessage({ error: true, text: errorMessage(err, "Speichern fehlgeschlagen.") });
        }
    };

    // Nacheinander speichern, damit sich schnelle Klicks nicht überholen.
    const move = async (index, dir) => {
        const next = moveInList(images, index, dir);
        setImages(next);
        setMoving(true);
        try {
            await api.put("/gallery/order", { ids: next.map((i) => i._id) });
        } catch (err) {
            setMessage({ error: true, text: errorMessage(err, "Sortieren fehlgeschlagen.") });
            load();
        } finally {
            setMoving(false);
        }
    };

    const remove = async (image) => {
        try {
            await api.delete(`/gallery/${image._id}`);
            setImages((list) => list.filter((i) => i._id !== image._id));
        } catch (err) {
            setMessage({ error: true, text: errorMessage(err, "Löschen fehlgeschlagen.") });
        }
    };

    return (
        <div>
            <div className="flex items-start justify-between gap-3">
                <PageTitle>Galerie</PageTitle>
                <SecondaryButton disabled={uploading} onClick={() => fileInput.current.click()}>
                    {uploading ? "Lädt hoch …" : "+ Fotos hochladen"}
                </SecondaryButton>
                <input
                    ref={fileInput}
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={(e) => upload(e.target.files)}
                />
            </div>
            <Hint className="mb-3.5">
                Reihenfolge mit ↑ ↓ ändern, das erste Bild erscheint zuerst auf der Website. Bildunterschrift wird beim
                Verlassen des Feldes gespeichert.
            </Hint>
            <Feedback message={message} />
            {images.map((image, index) => (
                <GalleryRow
                    key={image._id}
                    image={image}
                    index={index}
                    count={images.length}
                    moving={moving}
                    onMove={move}
                    onSave={save}
                    onDelete={remove}
                />
            ))}
            {loaded && images.length === 0 && <Empty>Noch keine Fotos. Laden Sie die ersten hoch.</Empty>}
        </div>
    );
}

